/**
 * React hook for WebSocket real-time updates.
 *
 * Provides:
 * - Automatic connection management
 * - Reconnection logic
 * - Message handling
 * - Connection status
 */

import { checkForUpdate } from '@/utils/updateCheck';
import { useState, useEffect, useCallback, useRef } from 'react';
import type { WebSocketMessage } from '@/types/api';

interface UseWebSocketReturn {
  connected: boolean;
  everConnected: boolean;
  sendMessage: (message: Partial<WebSocketMessage>) => void;
}

/**
 * Hook for WebSocket connection
 */
export function useWebSocket(
  url: string,
  onMessage?: (message: WebSocketMessage) => void
): UseWebSocketReturn {
  const [connected, setConnected] = useState(false);
  // Tracks whether we've successfully connected at least once.
  // Prevents showing the disconnect banner on initial page load.
  const [everConnected, setEverConnected] = useState(false);
  // Counts consecutive failed reconnect attempts; resets to 0 on successful open.
  // A ref, not state: nothing renders it, and every message or retry would
  // otherwise re-render the whole app (this hook lives in its root).
  const reconnectAttemptsRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number>();
  // Monotonically-increasing connection ID. Each ws.onclose/onopen checks
  // that its ID still matches, preventing stale handlers from mutating state
  // after a new connection has already been started.
  const connectionIdRef = useRef(0);
  // Hold the latest onMessage callback in a ref so the WebSocket's onmessage
  // handler always calls the current version without needing onMessage in
  // connect's dep array (which would cause a WS reconnect on every tab switch).
  const onMessageRef = useRef(onMessage);
  useEffect(() => { onMessageRef.current = onMessage; });

  const connect = useCallback(() => {
    const myId = ++connectionIdRef.current;
    try {
      const ws = new WebSocket(url);

      ws.onopen = () => {
        if (connectionIdRef.current !== myId) return; // stale
        reconnectAttemptsRef.current = 0;
        setConnected(true);
        setEverConnected(true);
        // A deploy restarts the server and drops this socket, so every (re)connect is
        // the moment to find out whether a newer build is now being served.
        checkForUpdate();
      };

      ws.onmessage = (event) => {
        if (connectionIdRef.current !== myId) return; // stale
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          onMessageRef.current?.(message);  // always calls the latest callback
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        if (connectionIdRef.current !== myId) return; // stale
        setConnected(false);

        // Exponential backoff: 2s → 4s → 8s → 15s (cap) between reconnect attempts.
        // Fast first retry handles brief blips; longer delays avoid hammering during
        // a deployment where the new container may take 10–30s to be ready.
        const attempt = ++reconnectAttemptsRef.current;
        const delay = Math.min(Math.pow(2, attempt - 1) * 2000, 15000);
        reconnectTimeoutRef.current = window.setTimeout(() => {
          if (connectionIdRef.current !== myId) return; // stale
          connect();
        }, delay);
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to create WebSocket connection:', err);
    }
  }, [url]);  // onMessage intentionally excluded — accessed via onMessageRef

  const sendMessage = useCallback((message: Partial<WebSocketMessage>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  }, []);

  useEffect(() => {
    connect();

    // Reconnect immediately when the tab becomes visible again after being hidden.
    // Browsers may throttle or drop WebSocket connections while the tab is in the
    // background; this ensures users see fresh state when they switch back.
    // Only a socket that is gone is replaced: one still CONNECTING is about to
    // open, and starting another beside it left the first open but orphaned —
    // a ghost connection the server kept broadcasting every edit to.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const ws = wsRef.current;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      connect();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      // Invalidate the current connection so its callbacks become no-ops
      // eslint-disable-next-line react-hooks/exhaustive-deps
      connectionIdRef.current++;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return {
    connected,
    everConnected,
    sendMessage,
  };
}
